import React from "react"
import {
  type AgingSummary,
  CategorySubtype,
  CategoryType,
  type DimensionSummary,
} from "@digits-graphql/frontend/graphql-bearer"
import { Flow, ReportKind } from "@digits-graphql/frontend/graphql-public"
import { buildZeroMoneyFlow } from "@digits-shared/helpers/moneyFlowHelper"
import numberHelper from "@digits-shared/helpers/numberHelper"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import styled from "styled-components"
import {
  AGING_COLUMN_OFFSET,
  columnsByComponentSize,
  TIME_COLUMNS,
} from "src/frontend/components/Shared/Layout/Components/Statements/AgingReport/AgingColumns"
import { SectionSummaryTitle } from "src/frontend/components/Shared/Layout/Components/Statements/shared"
import {
  useStatementComponentSize,
  useStatementKind,
} from "src/frontend/components/Shared/Layout/Components/Statements/StatementContext"
import {
  Amount,
  ParentRow,
  Row,
  SectionRow,
} from "src/frontend/components/Shared/Layout/Components/Statements/StatementRowShared"
import { type RowDetails } from "src/frontend/components/Shared/Layout/Components/Statements/toDetailsData"

const LargeAgingSectionRow = styled(SectionRow)`
  padding-right: 0;
  padding-top: 8px;
`

const StyledRow = styled(ParentRow)<{ $columnCount: number }>`
  padding-right: 0;
  grid-template-columns: repeat(${(props) => props.$columnCount}, minmax(0, 1fr));
  font-weight: ${fonts.weight.heavy};
  padding-top: 8px;
`

const StyledSectionSummaryTitle = styled(SectionSummaryTitle)<{ $colSpan: number }>`
  grid-column: span ${(props) => props.$colSpan};
  text-transform: none;
`

interface AgingSummaryStatementRowProps {
  rowId: string
  depth: number
  summary: AgingSummary
  details?: RowDetails
}

const PercentRow = styled(Row)`
  padding-right: 0;
  text-align: right;
  font-size: 12px;
  margin-top: -6px;
  color: ${colors.secondary80};

  div:first-child {
    padding-left: 20px;
  }

  div {
    padding-right: 7px;
  }
`

const PercentRowTitle = styled.div`
  grid-column: span ${AGING_COLUMN_OFFSET};
  text-align: left;
`

const idxToPercent = (dimensionSummary: DimensionSummary, idx: number) => {
  const total = dimensionSummary.summary.total.current?.total.value.amount
  const amountAtIdx = dimensionSummary.aging[idx]?.summary.total.current?.total?.value.amount
  if (!amountAtIdx || !total) {
    return numberHelper.formatPercentage(0)
  }
  return numberHelper.formatPercentage(
    numberHelper.bankersRounding(Math.abs(amountAtIdx / total) * 100)
  )
}

export const AgingSummaryStatementRow: React.FC<AgingSummaryStatementRowProps> = ({
  rowId,
  depth,
  summary: { dimensionSummary },
  details,
}) => {
  const componentSize = useStatementComponentSize()
  const kind = useStatementKind()
  const columns = columnsByComponentSize(componentSize)

  const total = (
    <Amount
      rowId={rowId}
      moneyFlow={
        dimensionSummary.summary.total.current?.total ??
        buildZeroMoneyFlow(kind === ReportKind.APAging ? Flow.Outbound : Flow.Inbound)
      }
    />
  )

  // TODO: Pick one or the other way of getting this
  const hoverDetails: RowDetails = details?.periodDetails?.hover
    ? details
    : {
        periodDetails: {
          hover: {
            // TODO: Get this from the API response
            entity: {
              active: true,
              displayKey: "1",
              id: "category-uuid",
              name: "Category",
              subtype: CategorySubtype.AccountsPayable,
              type: CategoryType.Expenses,
            },
            history: dimensionSummary,
          },
        },
      }

  if (componentSize.isExtraLarge || componentSize.isLarge) {
    return (
      <>
        <LargeAgingSectionRow id={rowId} depth={depth} details={hoverDetails}>
          <StyledSectionSummaryTitle $colSpan={AGING_COLUMN_OFFSET}>
            Total Outstanding {kind === ReportKind.APAging ? "Payables" : "Receivables"}
          </StyledSectionSummaryTitle>
          {dimensionSummary.aging.map((agingDimension, i) => (
            <Amount
              key={i}
              rowId={rowId}
              moneyFlow={
                agingDimension.summary.total.current?.total ??
                buildZeroMoneyFlow(kind === ReportKind.APAging ? Flow.Outbound : Flow.Inbound)
              }
            />
          ))}
          {total}
        </LargeAgingSectionRow>
        <PercentRow rowId={rowId} depth={depth}>
          <PercentRowTitle>Percentage of total</PercentRowTitle>
          {TIME_COLUMNS.map((_col, idx) => (
            <div key={idx}>{idxToPercent(dimensionSummary, idx)}</div>
          ))}
          <div>100%</div>
        </PercentRow>
      </>
    )
  }
  if (componentSize.isMedium) {
    return (
      <StyledRow id={rowId} depth={depth} $columnCount={columns.length} details={hoverDetails}>
        <StyledSectionSummaryTitle $colSpan={columns.length - 1}>
          Total Outstanding
        </StyledSectionSummaryTitle>
        {total}
      </StyledRow>
    )
  }
  return (
    <StyledRow id={rowId} depth={depth} $columnCount={2} details={hoverDetails}>
      <StyledSectionSummaryTitle $colSpan={1}>Total</StyledSectionSummaryTitle>
      {total}
    </StyledRow>
  )
}
